/**
 * Bulk-import spec for the `Locations` sheet of the organizer workbook.
 *
 * The `eventLocations` collection backs every map pin and every label
 * referenced by sessions / travel items. Coordinates can't be set
 * meaningfully from a spreadsheet (the admin's MapKit picker is what
 * verifies the address parts against Apple's Place database), so this
 * importer creates `verified: false`, `coordinates: null` rows. The
 * organizer can then open each location in the admin and use the picker
 * to pin coordinates without re-typing the address.
 *
 * Color is required on the doc but optional on import — when blank, we
 * fall back to a sensible default hex.
 */
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import { normalize } from "./importTypes";

/** Default pin color when the import row leaves `color` blank. */
const DEFAULT_LOCATION_COLOR = "#1565C0";

/** Loose hex check — accepts `#RGB`, `#RRGGBB`, with or without alpha. */
const HEX_COLOR_PATTERN = /^#?[0-9a-fA-F]{3,8}$/;

export interface RawLocationImportRow extends Record<string, string | undefined> {
  label?: string;
  color?: string;
  address?: string;
  city?: string;
  stateOrRegion?: string;
  country?: string;
  /** Free-form planning-only column; not stored. Stripped during validation. */
  notes?: string;
}

/**
 * Parsed payload — matches the eventLocations doc shape, minus
 * `eventId` / `coordinates` / `verified` (filled in server-side).
 */
export interface ParsedLocationImportRow {
  label: string;
  color: string;
  address: string | null;
  city: string | null;
  stateOrRegion: string | null;
  country: string | null;
}

function normalizeColor(raw: string | undefined): string | null {
  const value = normalize(raw);
  if (!value) return null;
  if (!HEX_COLOR_PATTERN.test(value)) return null;
  return value.startsWith("#") ? value : `#${value}`;
}

export const locationImportSpec: ImportSpec<
  RawLocationImportRow,
  ParsedLocationImportRow
> = {
  sheetName: "Locations",
  purpose:
    "Places: the venue, restaurants, hotels, meeting rooms. Sessions / Travel reference these by label.",
  columns: [
    { key: "label", note: "required — short, recognizable name" },
    {
      key: "color",
      note: "hex color used for the map pin (e.g. #1565C0). Defaults pick smart values in the admin.",
    },
    { key: "address", note: "street line" },
    { key: "city", note: "" },
    { key: "stateOrRegion", note: "state / province / region" },
    { key: "country", note: "" },
    {
      key: "notes",
      note: "free-form — context for the planner; not stored on the doc",
    },
  ],
  example: {
    label: "Mario's Trattoria",
    color: "#8E24AA",
    address: "412 Main Street",
    city: "Park City",
    stateOrRegion: "Utah",
    country: "United States",
    notes: "Private dining room booked for Friday 7pm",
  },
  validate(raw, lineNumber) {
    const label = normalize(raw.label);
    const color = normalizeColor(raw.color) ?? DEFAULT_LOCATION_COLOR;
    const address = normalize(raw.address) || null;
    const city = normalize(raw.city) || null;
    const stateOrRegion = normalize(raw.stateOrRegion) || null;
    const country = normalize(raw.country) || null;

    const base: Omit<ParsedRow<ParsedLocationImportRow>, "status" | "message"> = {
      lineNumber,
      data: { label, color, address, city, stateOrRegion, country },
    };

    if (!label) {
      return invalid(base, "Label is required");
    }
    if (raw.color && normalize(raw.color) && !normalizeColor(raw.color)) {
      return invalid(
        base,
        `Invalid color "${raw.color}". Use a hex like #1565C0.`,
      );
    }

    return pending(base);
  },
  dedupe(rows) {
    const seenLabels = new Set<string>();
    return rows.map((row) => {
      if (row.status !== "pending") return row;
      const key = row.data.label.toLowerCase();
      if (seenLabels.has(key)) {
        return {
          ...row,
          status: "invalid" as const,
          message: "Duplicate label within input",
        };
      }
      seenLabels.add(key);
      return row;
    });
  },
};
